// add flow optional chaning
import { getCoverLotItemIds, getCoverLots } from './coverLots';
import { createSelector } from 'reselect';
import { featuredIdsByCatalogSelector } from './catalogFeatured';
import { getBiddingInfo } from './biddingInfo';
import { getBuyNow } from './buyNow';
import { getCatalog } from './catalog';
import { getCatalogRegistration } from './catalogRegistration';
import { getItem } from './item';
import { getLiveCatalogData } from './console';
import { getLiveItemStatus } from './liveItemStatus';
import { getSavedItem } from './saveItem';
import { getSavedItemCount } from './savedItemCount';
import { getSavedSearchItem } from './savedSearchItem';
import { getSeller } from './seller';
import { getSellerCatalogCounts } from './sellerCatalogCounts';
import { getSellerPreviewCatalogs } from './sellerPreviewCatalogs';
import { getViewTime } from './recentItem';
import type { GlobalState } from '../rootReducer';
import { isItemPromoted } from './searchPromoted';

const globalStateSelector = (state: GlobalState) => state;
const idSelector = (state: GlobalState, id) => id;

export const getCatalogCardData = createSelector([globalStateSelector, idSelector], (state, catalogId) => {
    const catalog = getCatalog(state, catalogId);
    const coverLots = getCoverLots(state, catalogId);
    const seller = getSeller(state, catalog.sellerId);
    return { catalog, coverLots, seller };
});

export const getCatalogCardDatas = createSelector([globalStateSelector, idSelector], (state, catalogIds) =>
    catalogIds.map(id => getCatalogCardData(state, id)),
);

export const getLiveCatalogCardData = createSelector([globalStateSelector, idSelector], (state, catalogId) => {
    const { bidders } = getLiveCatalogData(state, catalogId);
    return {
        ...getCatalogCardData(state, catalogId),
        bidders,
    };
});

export const getLiveCatalogCardDatas = createSelector([globalStateSelector, idSelector], (state, catalogIds) => {
    return catalogIds.map(id => getLiveCatalogCardData(state, id));
});

export const getPreviewCatalogCardsData = createSelector([globalStateSelector, idSelector], (state, sellerId) => {
    const seller = getSeller(state, sellerId);
    return getSellerPreviewCatalogs(state, sellerId)
        .map(catalogPreview => {
            return { catalogPreview, seller };
        })
        .sort((a, b) => {
            return a.catalogPreview.saleStartTs - b.catalogPreview.saleStartTs;
        });
});

export const getItemCardData = createSelector([globalStateSelector, idSelector], (state, itemId) => {
    const item = getItem(state, itemId);
    const biddingInfo = getBiddingInfo(state, item.itemId);
    const buyNow = getBuyNow(state, item.itemId);
    const { catalogId, sellerId } = item;
    const catalog = getCatalog(state, catalogId);
    const catalogRegistration = getCatalogRegistration(state, catalogId);
    const savedItem = getSavedItem(state, itemId);
    const savedSearch = getSavedSearchItem(state, itemId);
    const seller = getSeller(state, sellerId);
    const saves = getSavedItemCount(state, itemId);
    const liveItemStatus = getLiveItemStatus(state, itemId);
    const viewed = getViewTime(state, itemId);
    const featured = featuredIdsByCatalogSelector(state, catalogId);
    const coverLots = getCoverLotItemIds(state, catalogId);
    const promoted =
        isItemPromoted(state, itemId) ||
        featured?.[catalogId]?.includes(itemId) ||
        coverLots?.[catalogId]?.includes(itemId);
    return {
        biddingInfo,
        buyNow,
        catalog,
        catalogRegistration,
        item,
        liveItemStatus,
        promoted,
        savedItem,
        savedSearch,
        saves,
        seller,
        viewed,
    };
});

export const getItemCardDatas = createSelector([globalStateSelector, idSelector], (state, itemIds) => {
    return itemIds.map(id => getItemCardData(state, id));
});

export const getLiveItemCardDatas = createSelector([globalStateSelector, idSelector], (state, itemIds) => {
    return itemIds.map(id => getLiveItemCardData(state, id));
});

export const getLiveItemCardData = createSelector([globalStateSelector, idSelector], (state, itemId) => {
    return {
        ...getItemCardData(state, itemId),
        saves: getSavedItemCount(state, itemId),
    };
});

export const getSellerCardData = createSelector([globalStateSelector, idSelector], (state, sellerId) => {
    const seller = getSeller(state, sellerId);
    const catalogCounts = getSellerCatalogCounts(state, sellerId);
    return { catalogCounts, seller };
});

export const getSellerCardDatas = createSelector([globalStateSelector, idSelector], (state, sellerIds) =>
    sellerIds.map(id => getSellerCardData(state, id)),
);
